// Build-your-own Essence Discovery box.
// "Packing a box" adds N of the catalog's own real 3ML sample variants to the cart as
// separate line items via /cart/add.js — no bundle app, no new SKUs. Catalog data is
// read from the section's own server-rendered JSON (see essence-discovery-builder.liquid)
// so prices/availability always match what Shopify actually has live.
(function () {
  function initBuilder(root) {
    const dataEl = root.closest('.edb').querySelector('[data-edb-catalog-data]');
    let catalog = [];
    try {
      catalog = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('Essence Discovery builder: catalog data failed to parse', e);
      return;
    }
    if (!catalog.length) return;

    const families = Array.from(new Set(catalog.flatMap((f) => f.family))).sort();

    const state = { size: 5, activeFamily: families[0], picked: [] };

    // Real, considered choice for THIS brand, not just a bug fix: "Verve & Vesper
    // is about not choosing quickly" is the product's own stated philosophy — a
    // customer who deliberately assembled 4 of 5 vials and then got interrupted
    // (a call, a closed tab, a refresh) should find their composition exactly as
    // they left it, not lose it silently. sessionStorage (not localStorage) is
    // deliberate: this is a live in-progress choice, not a saved cart or wishlist,
    // so it should survive a refresh/reopen within the same visit but not linger
    // into a totally separate later session.
    const storageKey = 'edb-picked-' + root.id;
    function savePicked() {
      try { sessionStorage.setItem(storageKey, JSON.stringify(state.picked)); } catch (e) { /* private mode, quota — non-fatal */ }
    }
    function loadPicked() {
      try {
        const raw = sessionStorage.getItem(storageKey);
        if (!raw) return;
        const ids = JSON.parse(raw);
        if (!Array.isArray(ids)) return;
        // Validate against the real, currently-live catalog — a variant that sold
        // out or was removed since the last visit is dropped rather than kept as
        // a phantom pick.
        const validIds = new Set(catalog.map((f) => f.variantId));
        state.picked = ids.filter((id) => validIds.has(id)).slice(0, state.size);
      } catch (e) { /* corrupted value — start clean, don't throw */ }
    }
    function clearPicked() {
      state.picked = [];
      try { sessionStorage.removeItem(storageKey); } catch (e) { /* non-fatal */ }
    }
    loadPicked();

    const screens = {};
    root.querySelectorAll('[data-edb-screen]').forEach((el) => {
      screens[el.dataset.edbScreen] = el;
    });
    // Step order for this flow — determines whether a transition reads as advancing
    // (intro -> ... -> review) or retreating, so forward/back can animate as opposite,
    // symmetric slides instead of an identical crossfade both ways.
    const screenOrder = ['intro', 'pack', 'review', 'lift', 'wrapped'];
    let currentScreen = 'intro';
    let screenTimer = null;
    // The frame's height always matches whichever screen is showing, measured from
    // real content rather than guessed — so nothing is ever taller than the frame
    // (no internal scrollbar) and nothing sits in dead extra space either.
    // .edb__builder's own `transition: height` (see CSS) animates the resize in sync
    // with the slide instead of snapping.
    function measureHeight(screenEl) {
      // Every .edb-screen is position:absolute; inset:0, which pins its own rendered
      // height to whatever the frame's CURRENT height already is — scrollHeight on
      // it just reads that back, not the content's real natural height. Briefly
      // lifting the position constraint (out of the absolute stack, into normal
      // flow) lets it size to its actual content before reverting; nothing paints
      // mid-script, so this causes no visible flash.
      const prevPosition = screenEl.style.position;
      screenEl.style.position = 'static';
      const h = screenEl.offsetHeight;
      screenEl.style.position = prevPosition;
      return h;
    }
    function setBuilderHeight(name) {
      // While the stage is a fullscreen takeover, height is 100dvh via CSS — measuring
      // content height here would fight that and is unnecessary work besides.
      if (root.dataset.stage === 'open') return;
      root.style.height = measureHeight(screens[name]) + 'px';
    }

    // ---- fullscreen stage ----
    // A focused fixed overlay, CSS-only — no WebGL anywhere in this builder. Real
    // reason, not a style choice: this builder tried real liquid-glass refraction
    // twice (a fork of the menu's own shader, its own private atmosphere object,
    // even an explicit WEBGL_lose_context release on close) and on real mobile
    // devices it STILL intermittently left the site's real menu rendering blank/
    // white afterward — a WebGL context-budget interaction that couldn't be reliably
    // reproduced or verified from this session's own testing tools, only on the
    // user's actual phone. After two failed fixes, per this project's own systematic-
    // debugging discipline (3+ failed fixes means question the approach, not patch
    // again), the right call is removing WebGL from this builder entirely rather
    // than guessing at a third timing fix — zero WebGL usage here means this builder
    // can structurally never affect the menu's WebGL budget again, on any device,
    // regardless of how either file changes later. CSS-only glass (see .edb-fcard
    // etc.) stays exactly as reliable as it's always been.
    const stageCloseBtn = root.querySelector('[data-edb-stage-close]');
    let stageLastFocused = null;
    let stageScrollY = 0;
    // Tracked across opens so it can be reverted first — SplitText.create() on an
    // element that's already split (from a previous open) splits the wrapper spans
    // it left behind too, nesting chars inside chars. That's the real cause of the
    // rapid blink/glitch on a second open, confirmed by the reported repro
    // (close, then reopen) exactly matching "create a fresh split every call, never
    // revert the last one."
    let plateSplit = null;

    function trapStageFocus(event) {
      if (event.key === 'Escape') {
        closeStage();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = root.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // GSAP + Flip (assets/gsap.min.js, assets/gsap-flip.min.js) — self-hosted, no
    // CDN, both free for commercial use since Webflow's acquisition of GreenSock.
    // Registered once here rather than per-call. Every path below still has a
    // plain-CSS fallback (the [data-stage="opening"/"closing"] rules in the CSS)
    // for the rare case the scripts fail to load — the stage still opens and
    // closes correctly, just without the cinematic morph.
    const hasFlip = !!(window.gsap && window.Flip);
    const hasSplitText = !!(window.gsap && window.SplitText);
    if (hasFlip) window.gsap.registerPlugin(window.Flip);
    if (hasSplitText) window.gsap.registerPlugin(window.SplitText);

    // Real, confirmed bug: opening and closing the stage repeatedly and fast (a
    // real user tapping the CTA, closing, tapping again) could start a new
    // openStage() before the previous closeStage()'s own Flip shrink had actually
    // settled — both GSAP timelines then ran against the same root element at
    // once, leaving root's Flip-applied inline transform/position stranded mid-
    // animation and the just-closed screen's composition panel visibly ghosted
    // over the product page underneath. One in-flight transition at a time; a
    // call that arrives while the other is still running is dropped rather than
    // queued — the user's next real tap (once settled) opens/closes cleanly.
    let stageBusy = false;

    function openStage(targetScreen) {
      if (stageBusy) return;
      stageBusy = true;
      // Body-lock via overflow:hidden, not position:fixed+negative-top — the older
      // trick's own scrollTo restore was visibly animating on real mobile browsers
      // (turned out to actually be the height-transition bug fixed separately below,
      // not the restore itself). overflow:hidden still needs its own scrollY
      // capture/restore though: toggling it does NOT reliably preserve scrollTop
      // across browsers — several (mobile Safari included) reset it to 0 once the
      // lock is lifted, since nothing was actually "saved", just hidden. Skipping
      // the restore is what caused closing to land at the top of the page.
      stageLastFocused = document.activeElement;
      stageScrollY = window.scrollY;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      // Real, known mobile Safari gap: overflow:hidden alone does not stop the
      // PAGE's own rubber-band overscroll from still visibly bouncing behind a
      // position:fixed overlay — confirmed the real cause of "it still scrolls"
      // (the product page peeking/bouncing behind the stage) even with the
      // stage's own overscroll-behavior:none (.edb__builder) and each screen's
      // overscroll-behavior:contain already in place, since rubber-banding
      // originates from the DOCUMENT's own scroll, not the fixed element
      // sitting on top of it. Restored in closeStage's settleClosed.
      document.documentElement.style.overscrollBehavior = 'none';
      document.body.style.overscrollBehavior = 'none';

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const incoming = targetScreen ? screens[targetScreen] : null;

      if (!hasFlip || reduceMotion) {
        // Plain-CSS fallback — same sequence as the signature menu's own open()
        // (perfumalink-menu.js): reduced-motion viewers skip straight to 'open',
        // everyone else gets the 'opening' keyframe fade. rAF alone can stall on a
        // throttled/backgrounded tab and leave data-stage stuck — a setTimeout
        // fallback guarantees the transition always finishes.
        root.style.height = '';
        if (incoming) {
          screens[currentScreen]?.classList.remove('edb-screen--active');
          incoming.classList.add('edb-screen--active');
          currentScreen = targetScreen;
        }
        root.dataset.stage = reduceMotion ? 'open' : 'opening';
        document.addEventListener('keydown', trapStageFocus);
        const finishOpen = () => { root.dataset.stage = 'open'; stageBusy = false; };
        window.requestAnimationFrame(finishOpen);
        window.setTimeout(finishOpen, 520);
        stageCloseBtn?.focus({ preventScroll: true });
        return;
      }

      // Cinematic path: capture the card's real current box (size + position) in
      // the page BEFORE any change, then let it become the fullscreen stage, then
      // hand the before/after rects to Flip — it morphs the actual card into the
      // fullscreen frame via GPU transforms (not by animating width/height/top/left
      // directly, which the layout engine would have to reflow every frame). New
      // screen content is invisible until the box is most of the way there, so the
      // brief GPU-scale distortion during the morph is never seen showing real
      // content — only the card's own material (background/border/shadow).
      const gsap = window.gsap;
      const Flip = window.Flip;
      const flipState = Flip.getState(root);

      // .edb__builder's own `transition: height` (for in-page screen switches) has
      // nothing to do while Flip fakes the resize via a GPU transform — but at the
      // instant Flip lands on the real final dimensions, a lingering CSS transition
      // would still pick that up and re-animate a second, redundant hop. Disabled
      // for the whole cinematic sequence, restored once it's actually finished.
      const prevRootTransition = root.style.transition;
      root.style.transition = 'none';

      root.style.height = '';
      if (incoming) {
        screens[currentScreen]?.classList.remove('edb-screen--active');
        incoming.classList.add('edb-screen--active');
        currentScreen = targetScreen;
      }
      root.dataset.stage = 'open';
      document.addEventListener('keydown', trapStageFocus);

      const cards = incoming ? incoming.querySelectorAll('.edb-fcard, .edb-tab') : [];
      if (incoming) gsap.set(incoming, { autoAlpha: 0 });
      // .edb-fcard carries its own unconditional CSS entrance (`animation: edb-fcard-in`,
      // used for the normal in-stage tab-switch reveal) — dormant while the pack
      // screen is display:none, so it restarts the instant this screen first
      // becomes visible, fighting GSAP for control of the same opacity/transform
      // at the exact same moment. Confirmed real cause of a first-open blink, not
      // guessed: disabled only for cards, only for this one entrance, restored
      // right after so tab switches still get their normal CSS reveal.
      const cardsWithAnim = incoming ? incoming.querySelectorAll('.edb-fcard') : [];
      cardsWithAnim.forEach((c) => { c.style.animation = 'none'; });
      if (cards.length) gsap.set(cards, { autoAlpha: 0, y: 16 });

      // A single luxury beat on first arrival at the pack screen — "Your Composition"
      // settles in character by character, like a plate being engraved, rather than
      // just fading up with everything else. One considered moment, not decoration
      // sprinkled everywhere (Silent Luxury: restraint over constant motion).
      // Revert any split left over from a previous open FIRST — re-splitting an
      // already-split element nests chars inside chars, which is what produced the
      // rapid blink/glitch on a second open.
      if (plateSplit) { plateSplit.revert(); plateSplit = null; }
      let split = null;
      const plateLabel = targetScreen === 'pack' ? incoming?.querySelector('.edb-box-count span') : null;
      if (plateLabel && hasSplitText) {
        split = window.SplitText.create(plateLabel, { type: 'chars', aria: 'auto' });
        plateSplit = split;
        gsap.set(split.chars, { autoAlpha: 0, y: 10 });
      }

      // Real, verified-live behavior, not assumed: Flip's absolute:true leaves the
      // element permanently pinned at inline position:absolute (plus min/max-width,
      // grid-area, transform bookkeeping) rather than handing back to this rule's
      // own position:fixed — an inline style beats a stylesheet rule that has no
      // !important, which this one doesn't. Clearing exactly what Flip added lets
      // [data-stage="open"]'s own fixed/inset:0/100dvh apply cleanly at rest.
      // Same rAF-stall insurance as the menu's own open() — see closeStage's
      // matching guard for the confirmed failure mode this protects against.
      let openSettled = false;
      const finishOpen = () => {
        if (openSettled) return;
        openSettled = true;
        stageBusy = false;
        gsap.set(root, { clearProps: 'position,top,left,width,height,minWidth,maxWidth,minHeight,maxHeight,gridArea,transform,translate,rotate,scale,padding' });
        root.style.transition = prevRootTransition;
        // If this is the fallback path (real tl.onComplete never fired — a stalled
        // rAF clock, confirmed to actually happen in this session's own testing
        // tools), the content tweens below may still be sitting at their opacity:0
        // starting point. Force them to their intended end-state so nothing is
        // ever permanently invisible — a real gap, not just a test artifact: any
        // sufficiently slow/throttled real device could hit the same stall.
        // clearProps, not autoAlpha:1 — autoAlpha always WRITES inline opacity/
        // visibility, which then permanently overrides the plain CSS class toggle
        // goTo() uses for every later in-stage screen switch (confirmed root cause:
        // pack's leftover inline opacity:1/visibility:inherit outlived this whole
        // GSAP sequence and silently defeated .edb-screen's own opacity:0 rule the
        // next time goTo() removed .edb-screen--active from it, leaving it visually
        // opaque — but pointer-events:none — stacked on top of whatever goTo()
        // actually activated). Clearing hands opacity/visibility back to the CSS
        // class rules, exactly like root's own clearProps call above already does.
        if (incoming) gsap.set(incoming, { clearProps: 'opacity,visibility' });
        if (split) gsap.set(split.chars, { clearProps: 'opacity,transform' });
        if (cards.length) gsap.set(cards, { clearProps: 'opacity,visibility,transform' });
        // Deliberately NOT restored: setting `animation` back to '' would let the
        // base CSS rule reapply, which RESTARTS the keyframe from opacity:0 on
        // these already-visible cards — a second, redundant flash right after GSAP
        // just finished fading them in. No cleanup needed either way: renderCatalog()
        // rebuilds fresh DOM nodes on every tab switch, so the next set of cards
        // never had this inline override and gets the normal CSS entrance untouched.
        stageCloseBtn?.focus({ preventScroll: true });
      };
      const tl = gsap.timeline({ onComplete: finishOpen });
      window.setTimeout(finishOpen, 1600);
      tl.add(Flip.from(flipState, { duration: 0.9, ease: 'power4.inOut', absolute: true }), 0);
      if (incoming) tl.to(incoming, { autoAlpha: 1, duration: 0.5, ease: 'power2.out' }, 0.4);
      if (split) {
        tl.to(split.chars, { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power3.out', stagger: 0.025 }, 0.55);
      }
      if (cards.length) {
        tl.to(cards, { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.out', stagger: { amount: 0.3, from: 'start' } }, 0.5);
      }
    }

    function closeStage() {
      if (stageBusy) return;
      stageBusy = true;
      document.removeEventListener('keydown', trapStageFocus);
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // Restore "Your Composition" to a single plain text node while the stage is
      // shut — not just before the next split, so nothing carries stray per-char
      // wrapper spans (and the aria-label workaround they use) into the closed
      // resting state.
      if (plateSplit) { plateSplit.revert(); plateSplit = null; }

      const settleClosed = () => {
        stageBusy = false;
        const htmlEl = document.documentElement;
        document.body.style.overflow = '';
        htmlEl.style.overscrollBehavior = '';
        document.body.style.overscrollBehavior = '';
        // Restore the real scroll position explicitly rather than trusting the
        // browser to have kept it under overflow:hidden (see openStage — it
        // often doesn't). scroll-behavior forced to 'auto' for this one call so
        // it lands instantly instead of animating under the theme's global
        // scroll-behavior:smooth (assets/base.css).
        const prevScrollBehavior = htmlEl.style.scrollBehavior;
        htmlEl.style.scrollBehavior = 'auto';
        htmlEl.style.overflow = '';
        window.scrollTo(0, stageScrollY);
        htmlEl.style.scrollBehavior = prevScrollBehavior;
        // preventScroll still matters on its own: focusing an element the browser
        // considers even slightly out of view triggers its OWN native
        // scroll-into-view, which obeys the theme's global scroll-behavior:smooth
        // (assets/base.css) — a visible animated scroll with no code call behind it.
        stageLastFocused?.focus({ preventScroll: true });
      };

      const swapToIntro = () => {
        Object.values(screens).forEach((s) => { delete s.dataset.enter; delete s.dataset.leave; });
        screens[currentScreen]?.classList.remove('edb-screen--active');
        screens.intro.classList.add('edb-screen--active');
        currentScreen = 'intro';
      };

      if (!hasFlip || reduceMotion) {
        // Same close sequence as the menu (perfumalink-menu.js close()): 'closing'
        // is a plain opacity transition, not a keyframe, matching the menu's own
        // 260ms fade-out exactly (see .edb__builder[data-stage="closing"] in the CSS).
        root.dataset.stage = 'closing';
        window.setTimeout(() => {
          delete root.dataset.stage;
          swapToIntro();
          settleClosed();
          // .edb__builder's own `transition: height` (built for animating BETWEEN
          // in-page screens) would otherwise pick up this change and animate the
          // resize over 340ms, showing intro's content at the previous screen's
          // size for a moment. Disabling it for this one snap keeps size, position
          // and content changing together in the same frame.
          const prevTransition = root.style.transition;
          root.style.transition = 'none';
          setBuilderHeight('intro');
          void root.offsetHeight;
          root.style.transition = prevTransition;
        }, reduceMotion ? 0 : 260);
        return;
      }

      // Cinematic path: fade the current screen's content out first (it's about to
      // be replaced with completely different content — intro's — so it's a plain
      // crossfade, not part of the Flip). Only once that's done do we swap to intro,
      // measure its real resting height, capture the (now fullscreen) box, and hand
      // the shrink to Flip — chained via onComplete rather than nested at a timeline
      // position, since the Flip state can't be captured until the swap has actually
      // happened, and settleClosed (scroll/focus restore) must wait for the real
      // shrink duration, not the outer timeline's pre-computed one.
      const gsap = window.gsap;
      const Flip = window.Flip;
      const outgoing = screens[currentScreen];

      const shrinkAndSettle = () => {
        swapToIntro();
        delete root.dataset.stage;
        const prevRootTransition = root.style.transition;
        root.style.transition = 'none';
        setBuilderHeight('intro');
        void root.offsetHeight;
        gsap.set(screens.intro, { autoAlpha: 0 });
        const flipState = Flip.getState(root);

        // Same rAF+setTimeout dual-guarantee as the menu's own open() (perfumalink-
        // menu.js) and this stage's own plain-CSS fallback path — a tab that
        // throttles/never ticks its rAF-driven clock (confirmed a real occurrence
        // in this session's own testing tools) could otherwise leave the shrink
        // tween's onComplete never firing, permanently stranding position:absolute
        // and the body scroll-lock. Guarded so real completion and the fallback
        // can't both run.
        let settled = false;
        const finishShrink = () => {
          if (settled) return;
          settled = true;
          // Same Flip cleanup as openStage, but height is deliberately excluded:
          // the resting in-page card has no CSS-declared height of its own (it
          // relies entirely on the inline px value setBuilderHeight('intro') just
          // set above) — clearing it here would drop back to the CSS placeholder
          // instead of the real measured height.
          gsap.set(root, { clearProps: 'position,top,left,width,minWidth,maxWidth,minHeight,maxHeight,gridArea,transform,translate,rotate,scale,padding' });
          root.style.transition = prevRootTransition;
          // clearProps, not autoAlpha:1 — same reasoning as openStage's finishOpen:
          // an inline opacity/visibility left on intro would outlive this whole
          // close sequence and override its own CSS class rule the next time
          // anything toggles .edb-screen--active on it. Fallback-path safety net
          // either way: never leave intro's content permanently invisible if the
          // real Flip onComplete never fired.
          gsap.set(screens.intro, { clearProps: 'opacity,visibility' });
          settleClosed();
        };
        Flip.from(flipState, { duration: 0.8, ease: 'power4.inOut', absolute: true, onComplete: finishShrink });
        window.setTimeout(finishShrink, 900);
        gsap.to(screens.intro, { autoAlpha: 1, duration: 0.4, ease: 'power2.out', delay: 0.25 });
      };

      // shrinkAndSettle sets up its OWN rAF-stall fallback internally, but only
      // once it actually runs — a stall in this first fade-out step would mean it
      // never runs at all. Needs the same guarantee at this entry point too.
      let outgoingSettled = false;
      const runShrink = () => {
        if (outgoingSettled) return;
        outgoingSettled = true;
        shrinkAndSettle();
      };
      if (outgoing) {
        gsap.to(outgoing, { autoAlpha: 0, y: -12, duration: 0.3, ease: 'power2.in', onComplete: runShrink });
        window.setTimeout(runShrink, 400);
      } else {
        runShrink();
      }
    }

    stageCloseBtn?.addEventListener('click', closeStage);
    function goTo(name) {
      if (name === currentScreen || !screens[name]) return;
      const forward = screenOrder.indexOf(name) > screenOrder.indexOf(currentScreen);
      const outgoing = screens[currentScreen];
      const incoming = screens[name];

      clearTimeout(screenTimer);
      // Clear any mid-flight enter/leave state from an interrupted transition so the
      // next one starts clean rather than jumping from a stale offset.
      Object.values(screens).forEach((s) => { delete s.dataset.enter; delete s.dataset.leave; });

      incoming.dataset.enter = forward ? 'right' : 'left';
      // Force layout so the entering screen's starting offset is committed before the
      // active class flips it to translateX(0) — otherwise both changes coalesce into
      // one frame and it never visibly slides in.
      void incoming.offsetWidth;

      outgoing.classList.remove('edb-screen--active');
      outgoing.dataset.leave = forward ? 'left' : 'right';
      incoming.classList.add('edb-screen--active');
      setBuilderHeight(name);
      currentScreen = name;

      // Real, confirmed bug (impeccable critique, DOM-verified): the review screen
      // only ever got rebuilt from the [data-edb-review-btn] click handler — any
      // OTHER path that lands here (this one included) left it showing whatever
      // was there last, stale against the live composition. Re-syncing here too,
      // not just there, means "review" can never show anything but current truth
      // regardless of how it was reached.
      if (name === 'review') renderReview();

      screenTimer = setTimeout(() => {
        delete outgoing.dataset.leave;
        delete incoming.dataset.enter;
      }, 320);
    }

    // Real shop money format (e.g. "Rs. {{amount}}"), passed from Liquid's own
    // shop.money_format — not scraped from a rendered DOM node, which other scripts
    // on the page (currency/locale widgets) can rewrite after our own render.
    const moneyFormat = root.dataset.moneyFormat || '{{amount}}';
    const [moneyPrefix, moneySuffix] = moneyFormat.split(/\{\{\s*amount\s*\}\}/);
    function money(cents) {
      // Liquid's raw price/variant.price objects are already integers in the shop's
      // smallest currency unit (e.g. 29900 for ₹299) — the same convention the
      // `money` filter expects, so no further ×100 scaling happens here.
      const amount = (cents / 100).toFixed(2);
      return moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount);
    }
    // The box's own real flat price — what the customer is actually charged
    // (a single line item, its own variant; see the real add-to-cart handler
    // below). Real, confirmed bug this fixes: the review screen used to show
    // the SUM of the 5 real item prices as "Total," which is never the real
    // charge — this is that real number.
    const boxPrice = Number(root.dataset.boxPrice) || 0;

    // Builds a flip-digit total inside `container`, animating from
    // `fromCents` to `toCents` (real currency amounts, not display strings).
    // Handles the two amounts having different digit counts (e.g. "1405.00"
    // shrinking to "999.00") by collapsing the extra leading character(s)
    // away instead of trying to flip a digit that has no real counterpart —
    // a leading zero on a shrinking price would be a wrong number, not just
    // an animation nicety.
    function buildTotalFlip(container, fromCents, toCents) {
      container.innerHTML = '';
      // Real, confirmed-on-device bug this fixes: a plain trailing space in
      // this text node was getting collapsed away at the boundary with the
      // next sibling's inline-flex formatting context ("Rs.999.00" instead
      // of "Rs. 999.00") — white-space:pre keeps the space exactly as
      // written regardless of what kind of box sits next to it.
      const prefix = document.createElement('span');
      prefix.textContent = moneyPrefix;
      prefix.style.whiteSpace = 'pre';
      container.appendChild(prefix);
      const row = document.createElement('span');
      row.style.display = 'inline-flex';
      row.style.overflow = 'hidden';
      container.appendChild(row);
      const suffix = document.createElement('span');
      suffix.textContent = moneySuffix;
      suffix.style.whiteSpace = 'pre';
      container.appendChild(suffix);

      const fromStr = (fromCents / 100).toFixed(2);
      const toStr = (toCents / 100).toFixed(2);
      const diff = fromStr.length - toStr.length;
      const digits = [];

      if (diff >= 0) {
        for (let i = 0; i < diff; i++) {
          const col = document.createElement('span');
          col.className = 'edb-flipdigit';
          col.textContent = fromStr[i];
          col.style.transition = 'width 380ms ease, opacity 380ms ease, margin 380ms ease';
          row.appendChild(col);
          digits.push({ el: col, collapse: true });
        }
        for (let i = diff; i < fromStr.length; i++) {
          const front = fromStr[i], back = toStr[i - diff];
          const col = document.createElement('span');
          col.className = 'edb-flipdigit';
          col.innerHTML =
            '<span class="edb-flipdigit__face edb-flipdigit__face--front">' + front + '</span>' +
            '<span class="edb-flipdigit__face edb-flipdigit__face--back">' + back + '</span>';
          row.appendChild(col);
          digits.push({ el: col, changed: front !== back });
        }
      } else {
        // Real total is longer than the retail sum (rare — a promo could make
        // the sum smaller than the box price) — same idea in reverse: the
        // extra leading character(s) grow in rather than collapse away.
        for (let i = 0; i < -diff; i++) {
          const col = document.createElement('span');
          col.className = 'edb-flipdigit';
          col.textContent = toStr[i];
          col.style.width = '0'; col.style.opacity = '0'; col.style.overflow = 'hidden';
          col.style.transition = 'width 380ms ease, opacity 380ms ease';
          row.appendChild(col);
          digits.push({ el: col, grow: true });
        }
        for (let i = 0; i < fromStr.length; i++) {
          const front = fromStr[i], back = toStr[i - diff];
          const col = document.createElement('span');
          col.className = 'edb-flipdigit';
          col.innerHTML =
            '<span class="edb-flipdigit__face edb-flipdigit__face--front">' + front + '</span>' +
            '<span class="edb-flipdigit__face edb-flipdigit__face--back">' + back + '</span>';
          row.appendChild(col);
          digits.push({ el: col, changed: front !== back });
        }
      }
      return digits;
    }

    // ---- intro screen ----
    // Fixed at 5 vials — no size choice screen. A second, larger option added real
    // decision fatigue right where we want a quick "yes", and cost more to redeem
    // against a full bottle than the format sustains.
    // Two real triggers share this one hook: the intro screen's own button, and the
    // "Customize Your Set" CTA now living in the real product-information section's
    // own product-details column (blocks/edb-customize-cta.liquid) — a separate
    // top-level section, not a descendant of root or even of .edb, since the product
    // now uses the theme's own default template (real gallery/title/price) with this
    // builder appended after it, rather than the builder rendering its own duplicate
    // hero. document-wide query, not root.closest('.edb'), for exactly that reason.
    document.querySelectorAll('[data-edb-start]').forEach((btn) => {
      btn.addEventListener('click', () => {
        // Not goTo('pack') — openStage('pack') does the screen swap itself, timed
        // against the container's own Flip morph (or the plain-CSS fallback's
        // entrance), since the container is jumping from an in-page card to a
        // fixed fullscreen frame at this exact instant. Running the normal slide
        // dance at the same moment would fight that container-level jump instead
        // of reading as one clean, single arrival.
        Object.values(screens).forEach((s) => { delete s.dataset.enter; delete s.dataset.leave; });
        openStage('pack');
      });
    });

    // ---- tabs ----
    const tabsEl = root.querySelector('[data-edb-tabs]');
    tabsEl.querySelectorAll('[data-edb-family]').forEach((btn) => {
      btn.addEventListener('click', () => {
        state.activeFamily = btn.dataset.edbFamily;
        tabsEl.querySelectorAll('[data-edb-family]').forEach((t) =>
          t.classList.toggle('edb-tab--active', t === btn)
        );
        renderCatalog();
        // Different families can wrap the tab row and size the catalog row
        // differently — resync the frame to the (now possibly different) real
        // content height for this still-active pack screen.
        setBuilderHeight('pack');
      });
    });

    // Real, confirmed gap: clearing a composition (Start Over, or a real
    // cart-add succeeding) reset the picks and the catalog cards, but left
    // whichever family tab was last active still selected — a genuinely
    // fresh start shouldn't strand the next customer on, say, "OUD" just
    // because the last composition happened to end there.
    function resetActiveTab() {
      const firstTab = tabsEl.querySelector('[data-edb-family]');
      if (!firstTab) return;
      state.activeFamily = firstTab.dataset.edbFamily;
      tabsEl.querySelectorAll('[data-edb-family]').forEach((t) => t.classList.toggle('edb-tab--active', t === firstTab));
    }

    // Real cognitive-load gap the critique flagged: switching tabs hid other-
    // family picks from view entirely. A small count badge per tab keeps "what's
    // already in the box" visible regardless of which family is active, without
    // duplicating the whole catalog on screen.
    function updateTabBadges() {
      tabsEl.querySelectorAll('[data-edb-family]').forEach((tab) => {
        const family = tab.dataset.edbFamily;
        const count = state.picked.reduce((n, id) => {
          const f = catalog.find((x) => x.variantId === id);
          return n + (f && f.family.includes(family) ? 1 : 0);
        }, 0);
        let badge = tab.querySelector('.edb-tab__count');
        if (count > 0) {
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'edb-tab__count';
            tab.appendChild(badge);
          }
          badge.textContent = String(count);
        } else if (badge) {
          badge.remove();
        }
      });
    }

    // ---- catalog ----
    const catalogEl = root.querySelector('[data-edb-catalog]');
    function renderCatalog() {
      // Full rebuild — only for a real change of item set (family switch, first
      // render). Picking/unpicking a card must NOT go through here: rebuilding every
      // node restarts each card's entrance animation, which is what made the whole
      // row visibly "blink" on every single add. See updateCardStates() for that.
      catalogEl.innerHTML = '';
      const items = catalog.filter((f) => f.family.includes(state.activeFamily));
      // Real, confirmed gap: an empty family used to leave a blank scrollable area
      // with no explanation, indistinguishable from a stuck loading state.
      if (!items.length) {
        catalogEl.innerHTML = '<p class="edb-catalog__empty">No fragrances in this family right now — try another.</p>';
        return;
      }
      items.forEach((f, i) => {
        const added = state.picked.includes(f.variantId);
        const full = state.picked.length >= state.size;
        const card = document.createElement('div');
        card.className = 'edb-fcard';
        card.dataset.variantId = f.variantId;
        card.style.animationDelay = i * 35 + 'ms';
        card.dataset.added = String(added);
        card.dataset.disabled = String(!added && full);
        card.innerHTML =
          '<div class="edb-fcard__head">' +
            '<img class="edb-fcard__thumb" src="' + f.image + '" alt="" loading="lazy">' +
            '<button type="button" class="edb-fcard__quickview">View</button>' +
          '</div>' +
          '<div class="edb-fcard__name">' + f.name + '</div>' +
          '<div class="edb-fcard__price">' + money(f.price) + ' · 3ml</div>' +
          '<button type="button" class="edb-fcard__add">' + (added ? 'Included ✓' : '+ Include') + '</button>';
        const addBtn = card.querySelector('.edb-fcard__add');
        // Real disabled attribute, not just CSS pointer-events:none — a screen
        // reader announced these as plain clickable buttons with no indication
        // they were inert (confirmed accessibility gap). Already-added items stay
        // enabled regardless of "full" so removing one is never blocked.
        addBtn.disabled = !added && full;
        addBtn.addEventListener('click', () => togglePick(f));
        card.querySelector('.edb-fcard__quickview').addEventListener('click', () => openQuickView(f));
        catalogEl.appendChild(card);
      });
    }

    function updateCardStates() {
      // In-place update of every already-rendered card's added/disabled state and
      // button label — no DOM node is destroyed or recreated, so nothing restarts
      // its entrance animation and the rest of the row stays perfectly still.
      const full = state.picked.length >= state.size;
      catalogEl.querySelectorAll('.edb-fcard').forEach((card) => {
        const variantId = Number(card.dataset.variantId);
        const added = state.picked.includes(variantId);
        card.dataset.added = String(added);
        card.dataset.disabled = String(!added && full);
        const addBtn = card.querySelector('.edb-fcard__add');
        addBtn.textContent = added ? 'Included ✓' : '+ Include';
        addBtn.disabled = !added && full;
      });
    }

    function pulse(el) {
      if (!el) return;
      el.classList.add('is-pulsing');
      setTimeout(() => el.classList.remove('is-pulsing'), 220);
    }

    let limitNoteTimer = null;
    function showLimitNote() {
      // Real feedback instead of a silent no-op when a 6th vial is attempted at
      // 5/5 — confirmed reading as "broken" rather than "at capacity" for anyone
      // who hasn't noticed the small 0/5 counter. aria-live (see the liquid
      // template) means screen-reader users hear this too.
      const note = root.querySelector('[data-edb-limit-note]');
      if (!note) return;
      note.textContent = 'Your composition is full — remove one to add another.';
      note.dataset.visible = 'true';
      clearTimeout(limitNoteTimer);
      limitNoteTimer = setTimeout(() => { note.dataset.visible = 'false'; }, 2600);
    }

    function togglePick(f) {
      const idx = state.picked.indexOf(f.variantId);
      const adding = idx === -1;
      if (!adding) {
        state.picked.splice(idx, 1);
      } else {
        if (state.picked.length >= state.size) { showLimitNote(); return; }
        state.picked.push(f.variantId);
      }
      savePicked();
      updateCardStates();
      updateBox();
      if (adding) {
        // Pulse the card and its new vial together, same moment — the two updates
        // should read as one connected action, not two unrelated UI changes.
        pulse(catalogEl.querySelector('[data-variant-id="' + f.variantId + '"]'));
        pulse(boxArtEl.querySelectorAll('.edb-slot')[state.picked.length - 1]);
      }
    }

    // ---- quick view ----
    // One reusable native <dialog> (see essence-discovery-builder.liquid), populated
    // from the same real catalog data the cards already render from — no extra fetch.
    const quickview = document.querySelector('[data-edb-quickview]');
    function openQuickView(f) {
      if (!quickview) return;
      quickview.querySelector('[data-edb-quickview-image]').src = f.image || '';
      quickview.querySelector('[data-edb-quickview-image]').alt = f.name;
      quickview.querySelector('[data-edb-quickview-family]').textContent = f.family.join(' · ');
      quickview.querySelector('[data-edb-quickview-name]').textContent = f.name;
      quickview.querySelector('[data-edb-quickview-price]').textContent = money(f.price) + ' · 3ml sample';
      quickview.querySelector('[data-edb-quickview-desc]').textContent = f.description || '';
      quickview.querySelector('[data-edb-quickview-link]').href = f.handle ? '/products/' + f.handle : '#';

      const addBtn = quickview.querySelector('[data-edb-quickview-add]');
      const added = state.picked.includes(f.variantId);
      const full = state.picked.length >= state.size;
      addBtn.textContent = added ? 'Included ✓' : '+ Add to Composition';
      addBtn.disabled = !added && full;
      addBtn.onclick = () => {
        togglePick(f);
        quickview.close();
      };

      if (typeof quickview.showModal === 'function') quickview.showModal();
    }
    quickview?.querySelector('[data-edb-quickview-close]')?.addEventListener('click', () => quickview.close());
    quickview?.addEventListener('click', (e) => {
      // Native <dialog> reports a click on its own backdrop area as a click on the
      // dialog element itself (the click target is the <dialog>, not a child) —
      // clicking anywhere that isn't the panel content closes it.
      if (e.target === quickview) quickview.close();
    });

    // ---- box art ----
    const boxArtEl = root.querySelector('[data-edb-box-art]');
    const vialSrc = root.dataset.vialSrc || '';
    function buildBoxArt() {
      // Real Verve & Vesper packaging: a dark case with a gold foil brand label and a
      // tray of standing vials — not a plain checklist. The vial is the real product's
      // transparent cutout PNG, dimmed/desaturated when empty, full color once picked.
      boxArtEl.innerHTML =
        '<div class="edb-box-art__label">' +
          '<span class="edb-box-art__brand">Verve &amp; Vesper</span>' +
          '<span class="edb-box-art__sub">Essence Discovery Collection</span>' +
        '</div>' +
        '<div class="edb-box-art__rack" data-edb-rack></div>';
      const rack = boxArtEl.querySelector('[data-edb-rack]');
      for (let i = 0; i < state.size; i++) {
        const slot = document.createElement('div');
        slot.className = 'edb-slot';
        slot.title = 'Vial ' + (i + 1);
        slot.innerHTML = '<img class="edb-slot__img" src="' + vialSrc + '" alt="" loading="lazy">';
        rack.appendChild(slot);
      }
      updateBox();
    }

    function updateBox() {
      const slots = boxArtEl.querySelectorAll('.edb-slot');
      slots.forEach((slot, i) => {
        const variantId = state.picked[i];
        const f = catalog.find((x) => x.variantId === variantId);
        if (f) {
          slot.title = f.name;
          slot.classList.add('is-filled');
        } else {
          slot.title = 'Vial ' + (i + 1);
          slot.classList.remove('is-filled');
        }
      });
      root.querySelector('[data-edb-count]').textContent = state.picked.length + ' / ' + state.size;
      const total = state.picked.reduce((sum, id) => {
        const f = catalog.find((x) => x.variantId === id);
        return sum + (f ? f.price : 0);
      }, 0);
      root.querySelector('[data-edb-total]').textContent = money(total);
      root.querySelector('[data-edb-review-btn]').disabled = state.picked.length < state.size;
      const resetBtn = root.querySelector('[data-edb-reset]');
      if (resetBtn) resetBtn.dataset.visible = String(state.picked.length > 0);
      updateTabBadges();
    }

    // Real power-user gap the critique flagged: no way to clear a composition
    // except removing picks one at a time. A full re-render (not updateCardStates)
    // since every card's added/disabled state changes at once.
    root.querySelector('[data-edb-reset]')?.addEventListener('click', () => {
      clearPicked();
      resetActiveTab();
      renderCatalog();
      updateBox();
    });

    // Single source of truth for the review screen's content — called both from
    // the button below AND from goTo('review') itself (see goTo), so it can never
    // go stale regardless of which path lands here. Also gives each row a real
    // inline remove control (was: back to pack, hunt for the same card, toggle it
    // off — a real user-control gap the critique flagged).
    function renderReview() {
      // Real, confirmed bug this fixes: returning here for a SECOND
      // composition (pack -> Review Your Composition again, without ever
      // clicking "Continue to Cart" first) left the first purchase's own
      // "Composed ✓" button label and "your composition of 5 vials has been
      // added to your cart" status line still showing, on top of the fresh
      // review list underneath — every other reset point (wrapped-done,
      // the add-to-cart error path, the bfcache pageshow handler) reset
      // these two, but entering review itself never did, so this was the
      // one real path that skipped it.
      addBtn.disabled = false;
      delete addBtn.dataset.composing;
      addBtn.querySelector('[data-edb-cta-label]').textContent = 'Compose & Reveal';
      statusEl.hidden = true;

      const listEl = root.querySelector('[data-edb-review-list]');
      listEl.innerHTML = '';
      let retailTotal = 0;
      state.picked.forEach((id, i) => {
        const f = catalog.find((x) => x.variantId === id);
        if (!f) return;
        retailTotal += f.price;
        const row = document.createElement('div');
        row.className = 'edb-review-row';
        row.style.animationDelay = i * 40 + 'ms';
        row.innerHTML =
          '<span class="edb-review-row__name">' + f.name + '</span>' +
          '<span class="edb-review-row__end">' +
            '<span>' + money(f.price) + '</span>' +
            '<button type="button" class="edb-review-row__remove" aria-label="Remove ' + f.name + '">&times;</button>' +
          '</span>';
        row.querySelector('.edb-review-row__remove').addEventListener('click', () => {
          togglePick(f);
          // Fewer than 5 now — nothing to review until it's rebuilt, so return to
          // pack rather than show a review screen for an incomplete composition.
          goTo('pack');
        });
        listEl.appendChild(row);
      });

      const realTotal = boxPrice || retailTotal;
      const poeticEl = root.querySelector('[data-edb-review-poetic]');
      const creditEl = root.querySelector('[data-edb-credit-note]');
      const wasEl = root.querySelector('[data-edb-review-was]');
      const redeemToggle = root.querySelector('[data-edb-redeem-toggle]');

      const hasRealGap = retailTotal !== realTotal;
      const hasSaving = retailTotal > realTotal;

      // Real, confirmed-on-device bug this fixes: text used to be set INSIDE
      // the delayed reveal callbacks below, so each line's real height only
      // existed from the instant it appeared — an empty <p> suddenly gaining
      // a full line of text is a hard, instant layout jump, not a smooth
      // reveal, and it shoved "Compose & Reveal" down each time. Setting the
      // final text now, up front, means every line's real height is already
      // reserved before any animation starts — only opacity/transform change
      // later, so nothing below ever moves.
      wasEl.textContent = hasRealGap ? money(retailTotal) : '';
      poeticEl.textContent = hasSaving ? 'The arithmetic favors you.' : '';
      creditEl.textContent = money(realTotal) + ' carries forward toward your first full bottle.';

      // Reset every reveal to its pre-animation state — renderReview() runs on
      // every real visit to this screen, not just the first, so a second
      // composition's review must restart the whole sequence rather than show
      // it already-settled from before.
      [poeticEl, creditEl].forEach((el) => { el.style.transition = 'none'; el.style.opacity = 0; el.style.transform = 'translateY(6px)'; });
      wasEl.style.transition = 'none'; wasEl.style.opacity = 0; wasEl.style.transform = 'translateY(-3px)';
      redeemToggle.style.transition = 'none'; redeemToggle.style.opacity = 0; redeemToggle.style.pointerEvents = 'none';

      const totalEl = root.querySelector('[data-edb-review-total]');
      const digits = buildTotalFlip(totalEl, retailTotal, realTotal);

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const flipDelay = reduceMotion ? 0 : 220;
      const flipStep = reduceMotion ? 0 : 130;
      let settleAt = flipDelay;

      digits.forEach((d, i) => {
        const at = flipDelay + i * flipStep;
        if (d.collapse) {
          window.setTimeout(() => {
            d.el.style.width = '0'; d.el.style.opacity = '0'; d.el.style.marginInline = '0';
          }, at);
        } else if (d.grow) {
          window.setTimeout(() => {
            d.el.style.width = '.62em'; d.el.style.opacity = '1';
          }, at);
        } else if (d.changed) {
          window.setTimeout(() => d.el.classList.add('is-flipping'), at);
        }
        settleAt = Math.max(settleAt, at + (reduceMotion ? 0 : 480));
      });

      // Once the total has actually settled: the muted original sum (only
      // when there's a real gap to show), then the poetic line (only when
      // that gap is a real saving, never claimed when it isn't). Text is
      // already in place (see above) — only opacity/transform animate here.
      window.setTimeout(() => {
        if (hasRealGap) {
          wasEl.style.transition = 'opacity 380ms ease, transform 380ms ease';
          wasEl.style.opacity = 1; wasEl.style.transform = 'translateY(0)';
        }
        if (hasSaving) {
          poeticEl.style.transition = 'opacity 420ms var(--edb-ease-out), transform 420ms var(--edb-ease-out)';
          poeticEl.style.opacity = 1; poeticEl.style.transform = 'translateY(0)';
        }
      }, settleAt);

      window.setTimeout(() => {
        creditEl.style.transition = 'opacity 380ms ease, transform 380ms ease';
        creditEl.style.opacity = 1; creditEl.style.transform = 'translateY(0)';
        redeemToggle.style.transition = 'opacity 380ms ease';
        redeemToggle.style.opacity = 1; redeemToggle.style.pointerEvents = 'auto';
      }, settleAt + (reduceMotion ? 0 : 520));
    }

    root.querySelector('[data-edb-review-btn]').addEventListener('click', () => {
      if (state.picked.length < state.size) return;
      renderReview();
      goTo('review');
    });

    // "How to Redeem" — a real popup (native <dialog>, same pattern as quick
    // view below), not an inline expand, per direct request.
    const redeemDialog = document.querySelector('[data-edb-redeem-dialog]');
    root.querySelector('[data-edb-redeem-toggle]')?.addEventListener('click', () => {
      if (typeof redeemDialog?.showModal === 'function') redeemDialog.showModal();
    });
    redeemDialog?.querySelectorAll('[data-edb-redeem-dialog-close]').forEach((btn) => {
      btn.addEventListener('click', () => redeemDialog.close());
    });
    redeemDialog?.addEventListener('click', (e) => {
      if (e.target === redeemDialog) redeemDialog.close();
    });

    root.querySelector('[data-edb-back]').addEventListener('click', () => goTo('pack'));

    // ---- wrapped (shareable) ----
    // A Spotify-Wrapped-style reveal shown AFTER a real cart-add succeeds — see
    // the liquid template's own comment for why this sits after, not before,
    // the purchase decision. This is the real build of the concept prototyped
    // earlier in the "Pack Your Own Box" artifact (deterministic seeded name +
    // generative ink mark + Explorer/Purist/Curator + rarest-pick + day/night
    // lean) — ported here against the REAL live catalog and REAL picks rather
    // than the artifact's static mock data, never placeholder content.
    const DAY_FAMILIES = ['Fresh', 'Floral'];
    const NIGHT_FAMILIES = ['Amber', 'Oud', 'Leather', 'Liquor'];
    const NAME_ADJ = {
      Amber: ['Quiet Ember', 'Low Flame', 'Warm Hour'],
      Floral: ['Bloom Hush', 'Pale Petal', 'Soft Bloom'],
      Fresh: ['Clear Morning', 'First Light', 'Open Air'],
      Gourmand: ['Honeyed Hush', 'Velvet Hour', 'Sweet Dusk'],
      Leather: ['Worn Dusk', 'Saddle & Smoke', 'Dark Grain'],
      Liquor: ['Gilded Barrel', 'Amber Pour', 'Late Toast'],
      Oud: ['Ancient Resin', 'Deep Root', 'The Long Dusk'],
      Spicy: ['Ember Trail', 'Ignite', 'Clove & Ash'],
      Woody: ['Cedar Grove', 'Root & Bark', 'The Quiet Wood'],
    };
    // FNV-1a — a real, tiny, dependency-free string hash, not a cryptographic
    // one (doesn't need to be): only needs to be deterministic and well-mixed
    // enough that two different compositions rarely land on the same seed.
    function hashString(str) {
      let h = 2166136261;
      for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
      }
      return h >>> 0;
    }
    // mulberry32 — a small seeded PRNG so "the same exact box always produces
    // the same name, mark and stats" is literally true, not just usually true
    // (Math.random() would make every reveal of the same composition different).
    function mulberry32(seed) {
      return function () {
        seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
        let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
    }
    // How rare each family is across the REAL live catalog (not the 5 picked) —
    // "Rarest Pick" means rarest in the whole collection, not rarest among the
    // 5 the customer happened to choose.
    function catalogRarity() {
      const counts = {};
      catalog.forEach((f) => f.family.forEach((fam) => { counts[fam] = (counts[fam] || 0) + 1; }));
      return counts;
    }
    function buildWrappedData(pickedIds) {
      const items = pickedIds.map((id) => catalog.find((f) => f.variantId === id)).filter(Boolean);
      const seed = hashString(pickedIds.slice().sort((a, b) => a - b).join('|'));
      const rand = mulberry32(seed);

      // Every family tag across all 5 picks counts (a pick can carry several —
      // see the liquid template's tag-mapping) — percentages are of total tags,
      // not of 5, matching the prototype's own real design.
      const counts = {};
      items.forEach((f) => f.family.forEach((fam) => { counts[fam] = (counts[fam] || 0) + 1; }));
      const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
      const totalTags = sorted.reduce((s, [, c]) => s + c, 0) || 1;
      const topFamily = sorted.length ? sorted[0][0] : 'Woody';

      const distinctFamilies = new Set(items.flatMap((f) => f.family)).size;
      const archetype = distinctFamilies >= 4
        ? 'An Explorer — your box spans ' + distinctFamilies + ' families'
        : distinctFamilies <= 2
          ? 'A Purist — devoted to ' + distinctFamilies + ' famil' + (distinctFamilies > 1 ? 'ies' : 'y')
          : 'A Curator — ' + distinctFamilies + ' families, chosen deliberately';

      const rarityCounts = catalogRarity();
      let rarest = null;
      items.forEach((f) => {
        f.family.forEach((fam) => {
          if (!rarest || rarityCounts[fam] < rarityCounts[rarest.fam]) {
            rarest = { name: f.name, fam, count: rarityCounts[fam] };
          }
        });
      });

      let day = 0, night = 0;
      items.forEach((f) => f.family.forEach((fam) => {
        if (DAY_FAMILIES.includes(fam)) day++;
        if (NIGHT_FAMILIES.includes(fam)) night++;
      }));
      const leanPct = day + night > 0 ? Math.round((night / (day + night)) * 100) : 50;
      const leanText = leanPct >= 65 ? leanPct + '% Night-leaning'
        : leanPct <= 35 ? (100 - leanPct) + '% Day-leaning'
        : 'Balanced — equally at home in day or night';

      const pool = NAME_ADJ[topFamily] || NAME_ADJ.Woody;
      const name = pool[Math.floor(rand() * pool.length)];

      // Generative ink-mark — six seeded points, smoothed into one continuous
      // stroke below. Deterministic from the same seed as the name, so this
      // exact composition always draws this exact mark, never a different one
      // on a repeat view.
      const points = [];
      for (let i = 0; i < 6; i++) {
        points.push({ x: 40 + rand() * 120, y: i * 44 + (rand() * 20 - 10) });
      }

      return {
        items, seed, sorted, totalTags, topFamily, name, archetype,
        rarest, leanText, points,
        subtitle: 'A box built around ' + topFamily + '.',
      };
    }

    // Smooth curve through seeded points via the standard "quadratic through
    // midpoints" technique — canvas has no SVG-style smooth-shorthand curve
    // command, so each midpoint becomes the real curve target and the raw
    // point becomes its control point.
    function drawInkMark(ctx, points, originX, originY) {
      ctx.beginPath();
      ctx.moveTo(originX + points[0].x, originY + points[0].y);
      for (let i = 1; i < points.length - 1; i++) {
        const midX = originX + (points[i].x + points[i + 1].x) / 2;
        const midY = originY + (points[i].y + points[i + 1].y) / 2;
        ctx.quadraticCurveTo(originX + points[i].x, originY + points[i].y, midX, midY);
      }
      const last = points[points.length - 1];
      ctx.quadraticCurveTo(originX + last.x, originY + last.y, originX + last.x, originY + last.y);
      ctx.strokeStyle = '#B28E5F';
      ctx.lineWidth = 1.6;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
      ctx.fillStyle = '#B28E5F';
      ctx.beginPath();
      ctx.arc(originX + points[0].x, originY + points[0].y, 3, 0, Math.PI * 2);
      ctx.fill();
    }

    // Real Perfumalink / Verve & Vesper marks, loaded from the store's own
    // Shopify CDN files (not a stand-in wordmark) — see settings.logo and the
    // uploaded Verve & Vesper wordmark file. Canvas taints (toBlob throws)
    // if a cross-origin image loads without CORS succeeding, which would
    // silently break Share/download entirely — so this resolves `null` on
    // any failure instead of throwing, and the caller always has a safe
    // text-wordmark fallback rather than a broken share feature.
    const LOGO_PL_URL = 'https://cdn.shopify.com/s/files/1/0672/1647/4186/files/133708.svg?v=1773133937';
    const LOGO_VV_URL = 'https://cdn.shopify.com/s/files/1/0672/1647/4186/files/Copy_of_VERVE_VERSPER_400_x_1600_px_2000_x_400_px__20251030_125334_0000.svg?v=1761809108';
    const logoCache = {};
    function loadLogo(url) {
      if (logoCache[url]) return logoCache[url];
      logoCache[url] = new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = url;
      });
      return logoCache[url];
    }
    // Draws an already-loaded logo image at a fixed height, centered
    // horizontally on cx, or falls back to a tracked-caps text wordmark when
    // the image failed to load / failed CORS.
    function drawLogoOrWordmark(ctx, img, fallbackText, cx, baselineY, targetH, color) {
      if (img) {
        const w = targetH * (img.naturalWidth / img.naturalHeight || 4);
        ctx.drawImage(img, cx - w / 2, baselineY - targetH, w, targetH);
        return w;
      }
      ctx.save();
      ctx.textAlign = 'center';
      ctx.fillStyle = color;
      ctx.font = '600 ' + Math.round(targetH * 0.85) + 'px "Cormorant Garamond", serif';
      ctx.letterSpacing = '2px';
      ctx.fillText(fallbackText, cx, baselineY);
      const w = ctx.measureText(fallbackText).width;
      ctx.restore();
      return w;
    }

    async function drawWrappedCanvas(canvas, data) {
      const ctx = canvas.getContext('2d');
      const w = canvas.width, h = canvas.height;
      const INK = '#25221F';
      const GOLD = '#B28E5F';
      const INK_SOFT = 'rgba(37, 34, 31, .58)';
      const INK_FAINT = 'rgba(37, 34, 31, .38)';

      // Real cream/parchment card material — same tokens as the rest of the
      // builder (--edb-cream / --edb-cream-deep), not the dark case gradient
      // this replaced. Canvas can't read CSS custom properties, so the hex
      // values are copied literally.
      const bg = ctx.createLinearGradient(0, 0, w * 0.25, h);
      bg.addColorStop(0, '#FBF7EF');
      bg.addColorStop(0.6, '#F8F2E9');
      bg.addColorStop(1, '#ECE0C9');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);
      ctx.strokeStyle = 'rgba(178, 142, 95, .35)';
      ctx.lineWidth = 2;
      ctx.strokeRect(24, 24, w - 48, h - 48);

      // Wait for the theme's own webfonts so the card doesn't silently fall
      // back to a system serif/sans on the very first render after page load.
      try { await document.fonts.ready; } catch (e) { /* non-fatal */ }
      const [logoPl, logoVv] = await Promise.all([loadLogo(LOGO_PL_URL), loadLogo(LOGO_VV_URL)]);

      // ---- brand lockup: Perfumalink × Verve & Vesper, real marks ----
      const lockupY = 108;
      const plW = drawLogoOrWordmark(ctx, logoPl, 'PERFUMALINK', w / 2 - 140, lockupY, 34, INK);
      ctx.save();
      ctx.textAlign = 'center';
      ctx.fillStyle = INK_FAINT;
      ctx.font = '400 22px Jost, sans-serif';
      ctx.fillText('×', w / 2, lockupY - 10);
      ctx.restore();
      drawLogoOrWordmark(ctx, logoVv, 'VERVE & VESPER', w / 2 + 150, lockupY, 26, INK);
      void plW;

      ctx.save();
      ctx.textAlign = 'center';
      ctx.strokeStyle = 'rgba(37, 34, 31, .18)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(w / 2 - 200, lockupY + 34);
      ctx.lineTo(w / 2 + 200, lockupY + 34);
      ctx.stroke();
      ctx.restore();

      // ---- eyebrow + composition number ----
      const compoNo = String((data.seed % 90) + 10);
      ctx.save();
      ctx.textAlign = 'center';
      ctx.fillStyle = INK_SOFT;
      ctx.font = '500 17px Jost, sans-serif';
      ctx.letterSpacing = '5px';
      ctx.fillText('YOUR COMPOSITION, UNWRAPPED  ·  NO. ' + compoNo, w / 2, lockupY + 76);
      ctx.restore();

      // ---- name + family + tagline ----
      ctx.textAlign = 'center';
      ctx.fillStyle = INK;
      ctx.font = 'italic 500 66px "Cormorant Garamond", serif';
      ctx.fillText(data.name, w / 2, lockupY + 160);

      ctx.save();
      ctx.fillStyle = GOLD;
      ctx.font = '600 16px Jost, sans-serif';
      ctx.letterSpacing = '4px';
      ctx.fillText(data.topFamily.toUpperCase(), w / 2, lockupY + 196);
      ctx.restore();

      ctx.fillStyle = INK_SOFT;
      ctx.font = 'italic 400 22px "Cormorant Garamond", serif';
      const subtitleY = lockupY + 236;
      ctx.fillText(data.subtitle, w / 2, subtitleY);

      // ---- radial diagram: the 5 real picks in orbit around the
      // composition's dominant family, not a bar/line chart. Deterministic
      // angle jitter from the same seed as the name/mark so this exact
      // composition always draws this exact layout.
      // Real, confirmed bug this fixes: hubCy used to be a flat offset from
      // lockupY that didn't account for the header block's real height —
      // the top orbit note's own center landed 2px from the subtitle's own
      // baseline, so its text and the subtitle text drew on top of each
      // other. Radii are also smaller here (190/86 vs the original 232/108)
      // so the whole diagram, stat row, never-existed panel and footer all
      // actually fit inside the fixed 1350px canvas height with real margin
      // between every block, not just the one collision that got noticed. ----
      const hubCx = w / 2, hubR = 76, orbitR = 190, noteR = 86;
      const topNoteClearance = 46;
      const hubCy = subtitleY + topNoteClearance + noteR + orbitR;
      const orbitRand = mulberry32(data.seed ^ 0x9E3779B9);

      ctx.save();
      ctx.strokeStyle = 'rgba(178, 142, 95, .32)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(hubCx, hubCy, orbitR, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(hubCx, hubCy, orbitR + 34, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();

      const angles = data.items.map((_, i) => (i / data.items.length) * Math.PI * 2 - Math.PI / 2 + (orbitRand() - 0.5) * 0.22);
      const notePositions = angles.map((a) => ({ x: hubCx + Math.cos(a) * orbitR, y: hubCy + Math.sin(a) * orbitR }));

      // connecting spokes + dot nodes, drawn under the circles
      notePositions.forEach((p) => {
        ctx.save();
        ctx.strokeStyle = 'rgba(178, 142, 95, .45)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(hubCx + (p.x - hubCx) * (hubR / orbitR), hubCy + (p.y - hubCy) * (hubR / orbitR));
        ctx.lineTo(hubCx + (p.x - hubCx) * ((orbitR - noteR * 0.86) / orbitR), hubCy + (p.y - hubCy) * ((orbitR - noteR * 0.86) / orbitR));
        ctx.stroke();
        ctx.fillStyle = GOLD;
        ctx.beginPath();
        ctx.arc(hubCx + (p.x - hubCx) * 1.14, hubCy + (p.y - hubCy) * 1.14, 3.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });

      // center hub
      ctx.save();
      ctx.fillStyle = 'rgba(255, 253, 247, .78)';
      ctx.strokeStyle = 'rgba(178, 142, 95, .55)';
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.arc(hubCx, hubCy, hubR, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.textAlign = 'center';
      ctx.fillStyle = INK;
      ctx.font = '600 11px Jost, sans-serif';
      ctx.letterSpacing = '1.5px';
      ctx.fillText('THE', hubCx, hubCy - 5);
      wrapCanvasText(ctx, 'COMPOSITION', hubCx, hubCy + 10, hubR * 1.7, 13);
      ctx.restore();

      // the 5 real picks, in orbit
      data.items.forEach((f, i) => {
        const p = notePositions[i];
        ctx.save();
        ctx.fillStyle = 'rgba(255, 253, 247, .82)';
        ctx.strokeStyle = 'rgba(178, 142, 95, .4)';
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.arc(p.x, p.y, noteR, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.textAlign = 'center';
        ctx.fillStyle = INK;
        ctx.font = '500 19px "Cormorant Garamond", serif';
        wrapCanvasText(ctx, f.name, p.x, p.y - 6, noteR * 1.6, 21);
        ctx.fillStyle = GOLD;
        ctx.font = '500 10px Jost, sans-serif';
        ctx.letterSpacing = '1.5px';
        ctx.fillText((f.family[0] || '').toUpperCase(), p.x, p.y + 28);
        ctx.restore();
      });

      // ---- stat row: You Are / Rarest Pick / Day-Night, three columns ----
      // Real, confirmed bug this fixes: this used to measure from hubCy+orbitR
      // (the orbit RING's radius), not the actual farthest point any note
      // CIRCLE reaches (+noteR) — the bottom-most note's own circle overlapped
      // this row before. +70 is real clearance below that true edge, not a
      // guess re-used from the old, wrong baseline.
      const statY = hubCy + orbitR + noteR + 70;
      const cols = [
        { k: 'YOU ARE', v: data.archetype },
        { k: 'RAREST PICK', v: data.rarest ? data.rarest.name : '—' },
        { k: 'DAY / NIGHT', v: data.leanText },
      ];
      const colW = (w - 200) / 3;
      cols.forEach((c, i) => {
        const cx = 100 + colW * i + colW / 2;
        if (i > 0) {
          ctx.save();
          ctx.strokeStyle = 'rgba(37, 34, 31, .18)';
          ctx.beginPath();
          ctx.moveTo(100 + colW * i, statY - 40);
          ctx.lineTo(100 + colW * i, statY + 40);
          ctx.stroke();
          ctx.restore();
        }
        ctx.save();
        ctx.textAlign = 'center';
        ctx.fillStyle = INK_FAINT;
        ctx.font = '600 13px Jost, sans-serif';
        ctx.letterSpacing = '2px';
        ctx.fillText(c.k, cx, statY - 14);
        ctx.fillStyle = INK;
        ctx.font = 'italic 500 22px "Cormorant Garamond", serif';
        wrapCanvasText(ctx, c.v, cx, statY + 18, colW - 24, 26);
        ctx.restore();
      });

      // ---- "never existed before" panel with the real generative mark in
      // place of a fingerprint icon (a fingerprint is a generic uniqueness
      // cliché that could sit on any brand — this composition's own ink
      // mark, seeded the same as its name above, cannot). ----
      // Real, confirmed bug this fixes: panelY + panelH used to land at 1292
      // on a 1350px-tall canvas — 2px from the footer text below it. +100/170
      // (was +90/190) gives real clearance against the footer instead of
      // nearly touching it.
      const panelY = statY + 100, panelH = 170, panelX = 90, panelW = w - 180;
      ctx.save();
      ctx.strokeStyle = 'rgba(178, 142, 95, .5)';
      ctx.lineWidth = 1.4;
      ctx.strokeRect(panelX, panelY, panelW, panelH);
      ctx.restore();

      drawInkMark(ctx, data.points, panelX + 46, panelY + 30);

      ctx.save();
      ctx.textAlign = 'left';
      ctx.fillStyle = INK;
      ctx.font = '600 20px Jost, sans-serif';
      ctx.letterSpacing = '1px';
      ctx.fillText('THIS EXACT COMPOSITION', panelX + 190, panelY + 60);
      ctx.fillText('HAS NEVER EXISTED BEFORE.', panelX + 190, panelY + 86);
      ctx.fillStyle = INK_FAINT;
      ctx.font = '400 16px Jost, sans-serif';
      ctx.letterSpacing = '1px';
      ctx.fillText('Composition ' + data.seed.toString(16).toUpperCase(), panelX + 190, panelY + 122);
      ctx.restore();

      // ---- footer ----
      ctx.save();
      ctx.textAlign = 'center';
      ctx.fillStyle = INK_SOFT;
      ctx.font = '600 17px Jost, sans-serif';
      ctx.letterSpacing = '5px';
      ctx.fillText('WWW.PERFUMALINK.COM', w / 2, h - 56);
      ctx.restore();
    }
    // Canvas has no native text-wrapping — breaks a long title across lines by
    // real measured width rather than a fixed character count, which would
    // either overflow a wide font or waste a line on a narrow one.
    function wrapCanvasText(ctx, text, cx, y, maxWidth, lineHeight) {
      const words = text.split(' ');
      let line = '';
      const lines = [];
      words.forEach((word) => {
        const test = line ? line + ' ' + word : word;
        if (ctx.measureText(test).width > maxWidth && line) {
          lines.push(line);
          line = word;
        } else {
          line = test;
        }
      });
      if (line) lines.push(line);
      const startY = y - ((lines.length - 1) * lineHeight) / 2;
      lines.forEach((l, i) => ctx.fillText(l, cx, startY + i * lineHeight));
    }

    const wrappedCanvas = root.querySelector('[data-edb-wrapped-canvas]');
    let wrappedBlob = null;
    async function openWrapped(pickedIds) {
      if (liftOrb) liftOrb.setPaused(true);
      const data = buildWrappedData(pickedIds);
      await drawWrappedCanvas(wrappedCanvas, data);
      wrappedBlob = await new Promise((resolve) => wrappedCanvas.toBlob(resolve, 'image/png'));
      goTo('wrapped');
    }

    root.querySelector('[data-edb-wrapped-share]')?.addEventListener('click', async () => {
      if (!wrappedBlob) return;
      const file = new File([wrappedBlob], 'verve-and-vesper-wrapped.png', { type: 'image/png' });
      // Native share sheet where the platform actually supports sharing a file
      // (most mobile browsers) — this is the real "post this" viral path, not
      // just a picture the customer has to manually attach somewhere.
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: 'My Verve & Vesper Composition' });
          return;
        } catch (e) {
          // User cancelled the share sheet, or the platform rejected it after
          // all — fall through to the download path below rather than leave
          // the tap looking like it did nothing.
        }
      }
      const url = URL.createObjectURL(wrappedBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'verve-and-vesper-wrapped.png';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    });

    root.querySelector('[data-edb-wrapped-done]')?.addEventListener('click', () => {
      // Real, pre-existing gap this path exposes: nothing ever reset the cart
      // button after a successful add (only the error path did), so starting
      // a second composition in the same page session — pick 5 more, land on
      // review again — found the button permanently stuck disabled on
      // "Composed ✓" from the first purchase. Reset here, the real point
      // where one composition's journey ends and the builder returns to rest.
      addBtn.disabled = false;
      delete addBtn.dataset.composing;
      addBtn.querySelector('[data-edb-cta-label]').textContent = 'Compose & Reveal';
      closeStage();
    });

    // ---- lift & wrap ----
    // A rare, once-per-purchase celebration between a real cart-add succeeding
    // and the Wrapped reveal — CSS-driven (see essence-discovery-builder.css),
    // not GSAP: the sequence is entirely predetermined and never interrupted
    // mid-flight by user input, exactly the case a plain CSS animation (runs
    // off the main thread) is the right tool for over a JS-driven one.
    // Deliberately NOT a click-gated choice screen (Reveal/Skip/Back) — tried
    // that, and it added a real interaction step after a purchase has already
    // completed, which is exactly the added friction this project's own
    // conversion research argues against. Pure animation, auto-forwarding,
    // same as the cart-add success path that leads into it.
    const liftScreenEl = screens.lift;
    function playLiftThenWrap(pickedIds) {
      goTo('lift');
      if (liftOrb) liftOrb.setPaused(false);
      // Restart cleanly even on a second purchase in the same page session
      // (build another composition, add to cart again) — re-adding the same
      // class name is a no-op; remove, force reflow, re-add is the standard
      // way to make a CSS animation actually restart from its 0% keyframe.
      liftScreenEl.classList.remove('is-playing');
      void liftScreenEl.offsetWidth;
      liftScreenEl.classList.add('is-playing');
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // Real requested timing: the whole lift+wrap moment is 6s — top ribbon
      // (1500ms delay + 1650ms) ends at 3150ms, side ribbon (3150ms delay +
      // 1500ms) ends at 4650ms, the bow (4650ms delay + 780ms) ends at
      // 5430ms, leaving ~570ms hold on the fully-wrapped, settled box before
      // cutting to the reveal. Every CSS duration/delay is the original
      // 4000ms choreography scaled by 1.5 — not independently re-tuned, so
      // the relative feel (lift, THEN ribbon, THEN bow) stays intact.
      // Skipped almost entirely under reduced motion, matching this file's
      // existing "fewer and gentler, never an unannounced jump cut" pattern.
      window.setTimeout(() => openWrapped(pickedIds), reduceMotion ? 200 : 6000);
    }

    // ---- real add to cart ----
    // One real line item, not five: the box's own product/variant (already the
    // single "Default Title" variant — see templates/product.essence-discovery.json)
    // is what actually goes to cart, carrying the 5 chosen names as line item
    // properties (Shopify renders these under the line item automatically, in
    // cart drawer and checkout alike). The five underlying sample variants are
    // deliberately NOT added as separate lines anymore — a real, disclosed
    // tradeoff: their own individual inventory no longer decrements on this path,
    // only the box product's own inventory does.
    const addBtn = root.querySelector('[data-edb-add-to-cart]');
    const statusEl = root.querySelector('[data-edb-cart-status]');
    const boxVariantId = root.dataset.boxVariantId;

    // thinking-orbs (assets/thinking-orbs.js) — real async-wait indicators,
    // not decoration: the button's own real fetch below, and the lift
    // screen's own real (if fixed-length) "Composing your box…" wait.
    // Guarded on window.ThinkingOrbs existing — a vendored file can fail to
    // load same as any other script, and the button/lift caption both
    // already have real text conveying the same state without it.
    const ctaOrbEl = root.querySelector('[data-edb-cta-orb]');
    const ctaOrbCanvas = root.querySelector('[data-edb-cta-orb-canvas]');
    const ctaOrb = (window.ThinkingOrbs && ctaOrbCanvas)
      // Real, confirmed bug this fixes: theme:'light' draws dark/ink-colored
      // dots — invisible against this button's own dark ink background
      // (.edb-cta { background: var(--edb-ink); }). 'dark' theme draws
      // near-white dots instead, same as the lift screen's own orb, which
      // sits on the same dark case material for the same reason.
      ? window.ThinkingOrbs.create(ctaOrbCanvas, { state: 'composing', size: 26, theme: 'dark', paused: true })
      : null;
    const liftOrbCanvas = root.querySelector('[data-edb-lift-orb-canvas]');
    const liftOrb = (window.ThinkingOrbs && liftOrbCanvas)
      ? window.ThinkingOrbs.create(liftOrbCanvas, { state: 'composing', size: 32, theme: 'dark', paused: true })
      : null;

    addBtn.addEventListener('click', async () => {
      if (state.picked.length < state.size || !boxVariantId) return;
      addBtn.disabled = true;
      // A real, designed beat — not however long the network happens to
      // take. Runs alongside the real fetch below (never blocks or delays
      // the actual cart mutation), and is awaited right before the success
      // state shows, so "Composing…" always reads for a real, consistent
      // 2.5s regardless of connection speed. Skipped entirely on error —
      // if something's actually broken, showing that immediately is more
      // useful than padding out a fake wait.
      const minComposingDelay = new Promise((resolve) => setTimeout(resolve, 3000));
      // data-composing, not just :disabled — :disabled's own opacity:.35
      // (see .edb-cta:disabled) is right for an inert button, wrong for one
      // doing real, visible work. This state gets its own full-opacity,
      // lifted-off-the-page look instead (see the CSS).
      addBtn.dataset.composing = 'true';
      addBtn.querySelector('[data-edb-cta-label]').textContent = 'Composing…';
      statusEl.hidden = true;
      if (ctaOrb) { ctaOrbEl.hidden = false; ctaOrb.setPaused(false); }

      // Real theme cart integration, not a private fetch the rest of the site
      // never hears about — same CartLinesUpdateEvent the theme's own
      // assets/product-form.js dispatches, which is what the cart drawer and
      // header cart icon (assets/cart-drawer.js, assets/cart-icon.js) actually
      // listen for to refresh and auto-open. Loaded from Shopify's own CDN module
      // (the exact URL this theme's importmap already points "@shopify/events"
      // at — see snippets/scripts.liquid) via dynamic import so this file doesn't
      // need to become an ES module just for this one call.
      let CartLinesUpdateEvent = null;
      try {
        ({ CartLinesUpdateEvent } = await import('https://cdn.shopify.com/storefront/standard-events.js'));
      } catch (e) {
        // CDN unreachable/offline — the add-to-cart itself still works below,
        // just without the drawer auto-opening for this one add.
      }
      const deferred = CartLinesUpdateEvent?.createPromise?.();
      if (CartLinesUpdateEvent && deferred) {
        document.dispatchEvent(new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: 'gid://shopify/ProductVariant/' + boxVariantId, quantity: 1 }],
          promise: deferred.promise,
        }));
      }

      const properties = {};
      // Captured now, before clearPicked() below resets state.picked — the
      // Wrapped card (see openWrapped) needs the real chosen fragrances after
      // the success path has already cleared the in-progress composition.
      const pickedSnapshot = state.picked.slice();
      state.picked.forEach((id, i) => {
        const f = catalog.find((x) => x.variantId === id);
        if (f) properties['Vial ' + (i + 1)] = f.name;
      });

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(boxVariantId), quantity: 1, properties }] }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.description || 'Could not add this box to your cart.');
        }
        if (CartLinesUpdateEvent && deferred) {
          const ajaxCart = await fetch('/cart.js').then((r) => r.json());
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
            detail: { items: ajaxCart.items, source: 'essence-discovery-builder', itemCount: 1 },
          });
        }
        await minComposingDelay;
        delete addBtn.dataset.composing;
        statusEl.dataset.state = 'ok';
        statusEl.hidden = false;
        statusEl.textContent = 'Your composition of ' + state.picked.length + ' essences has been added to your cart.';
        addBtn.querySelector('[data-edb-cta-label]').textContent = 'Composed ✓';
        if (ctaOrb) { ctaOrb.setPaused(true); ctaOrbEl.hidden = true; }
        // Real purchase completed — the saved-in-progress composition is no longer
        // "in progress," so clear it rather than let a stale sessionStorage entry
        // silently pre-fill a future visit's box with an already-bought set.
        // Real, confirmed bug this fixes: clearPicked() alone only resets the
        // in-memory state — it never touches the fragrance cards' own
        // "Included ✓" DOM or the box-panel bottle art/count/total, so a
        // second composition in the same session visually inherited the
        // first one's picks until the page was reloaded. renderCatalog() +
        // updateBox() are the exact same pairing the "Start Over" path
        // already calls right after its own clearPicked() — this path needs
        // both, not just the first (a real gap in the previous fix: the
        // fragrance cards refreshed but the box panel's bottle slots/count
        // still showed the just-purchased composition).
        clearPicked();
        resetActiveTab();
        renderCatalog();
        updateBox();
        // Real cart-add has already succeeded and the drawer event above has
        // already fired — the Lift & Wrap moment and the Wrapped reveal it
        // leads into (see playLiftThenWrap/openWrapped) are a bonus on top of
        // a completed purchase step, not a gate in front of it. "Composed ✓"
        // shows for a beat first so the button itself still confirms success
        // before the screen changes underneath it.
        window.setTimeout(() => playLiftThenWrap(pickedSnapshot), 700);
      } catch (err) {
        if (CartLinesUpdateEvent && deferred) deferred.reject(err);
        statusEl.dataset.state = 'error';
        statusEl.hidden = false;
        statusEl.textContent = err.message || 'Something went wrong adding this box.';
        addBtn.disabled = false;
        delete addBtn.dataset.composing;
        addBtn.querySelector('[data-edb-cta-label]').textContent = 'Compose & Reveal';
        if (ctaOrb) { ctaOrb.setPaused(true); ctaOrbEl.hidden = true; }
      }
    });

    renderCatalog();
    buildBoxArt();
    setBuilderHeight(currentScreen);
    // Content (fonts, wrapped tab labels) can settle a frame or two after load,
    // slightly changing real content height — resync once after that without any
    // visible animation (no screen is transitioning at this point).
    window.addEventListener('load', () => setBuilderHeight(currentScreen), { once: true });
    // Viewport width changes how tabs wrap and how many catalog cards show per row,
    // which changes the active screen's real content height — resync on resize too.
    let resizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => setBuilderHeight(currentScreen), 150);
    });

    // Real, confirmed bug: navigating back to this page can restore it from
    // the browser's bfcache (back/forward cache) — the page is unfrozen with
    // whatever JS state it had at the moment of navigating away, not reloaded.
    // If that moment was mid-transition (stageBusy left true by an in-flight
    // open/close, or the Review/Add-to-Cart buttons left mid-animation), every
    // future click silently no-ops against the `if (stageBusy) return` guards
    // in openStage/closeStage — confirmed the real cause of "first click does
    // nothing, second click works" specifically after using the browser's
    // back button. event.persisted is true only for an actual bfcache
    // restore, never a normal fresh load, so this never touches first-visit
    // behavior.
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      stageBusy = false;
      delete root.dataset.stage;
      liftScreenEl.classList.remove('is-playing');
      Object.values(screens).forEach((s) => { delete s.dataset.enter; delete s.dataset.leave; });
      screens[currentScreen]?.classList.remove('edb-screen--active');
      screens.intro.classList.add('edb-screen--active');
      currentScreen = 'intro';
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.documentElement.style.overscrollBehavior = '';
      document.body.style.overscrollBehavior = '';
      // Mirrors the real add-to-cart success path's own reset (see above) —
      // a bfcache restore can just as easily land mid "Composing…"/"Composed ✓"
      // as any other in-flight state.
      addBtn.disabled = false;
      delete addBtn.dataset.composing;
      addBtn.querySelector('[data-edb-cta-label]').textContent = 'Compose & Reveal';
      setBuilderHeight('intro');
    });
  }

  function init() {
    document.querySelectorAll('[data-edb-root]').forEach(initBuilder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
